import React, { useState, useEffect } from 'react';
import { BookOpen, AlertTriangle, Info, Link, Calendar, Tag, FileText, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Methodology = ({ onSubmit, initialData = {} }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    gwpVersion: 'AR5',
    calculationVersion: 'Calc v1',
    calculationTimestamp: new Date().toISOString(),
    methodology: '',
    boundaries: '',
    exclusions: '',
    scope3Methods: '',
    unitConversions: '',
    mbPolicy: '',
    assumptions: [],
    ...initialData
  });

  // Load initial data when component mounts or initialData changes
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData(prev => ({
        ...prev,
        ...initialData
      }));
    }
  }, [initialData]);

  // Auto-save to localStorage whenever formData changes
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0) {
      try {
        const currentData = JSON.parse(localStorage.getItem('calculationWorkflowData') || '{}');
        const updatedData = {
          ...currentData,
          methodology: formData
        };
        localStorage.setItem('calculationWorkflowData', JSON.stringify(updatedData));
      } catch (error) {
        console.error('Error saving Methodology data to localStorage:', error);
      }
    }
  }, [formData]);

  const [errors] = useState({});
  const [warnings] = useState({});

  // Function to generate sample methodology with current timestamp
  const generateSampleMethodology = () => {
    return `# Calculation Methodology

## Emission Factors & Sources

### Scope 1 Factors
- **Natural Gas**: 0.202 kg CO₂e/kWh (DEFRA 2023)
- **Diesel**: 2.68 kg CO₂e/L (IEA 2023)
- **Propane**: 3.01 kg CO₂e/kg (GLEC 2023)
- **Refrigerant R-134a**: 1430 kg CO₂e/kg (EPA eGRID 2023)

### Scope 2 Factors
- **Location-based**: Grid averages by region
  - California: 0.312 kg CO₂e/kWh
  - Bavaria: 0.485 kg CO₂e/kWh
  - Mazovia: 0.756 kg CO₂e/kWh
- **Market-based**: Supplier-specific contracts and residual mix

### Scope 3 Factors
- **Transport**: Distance-based factors by mode
- **Waste**: Fuel-based factors for disposal
- **Business Travel**: Flight distance class factors

## GWP Version
Using **AR5** (IPCC Fifth Assessment Report) with GWP values:
- CH₄ = 25 kg CO₂e
- N₂O = 298 kg CO₂e

## Boundaries & Exclusions

### Organizational Boundaries
- **Operational Control**: All facilities under operational control included
- **Financial Control**: Joint ventures with >50% ownership included

### Exclusions
- **Employee Commuting**: Excluded - not material (<1% of total emissions)
- **Downstream Leased Assets**: Excluded - no significant leased assets
- **Franchises**: Excluded - no franchise operations

## Scope 3 Methods by Category

### Category 4: Upstream Transportation
- **Method**: Distance-based calculation
- **Data Source**: Logistics provider reports
- **Coverage**: 95% of purchased goods

### Category 5: Waste Generated in Operations
- **Method**: Fuel-based calculation
- **Data Source**: Waste management contracts
- **Coverage**: 100% of waste streams

### Category 6: Business Travel
- **Method**: Flight distance class calculation
- **Data Source**: Travel booking system
- **Coverage**: 90% of business travel

### Category 9: Downstream Transportation
- **Method**: Tonne-km-based calculation
- **Data Source**: Distribution partner data
- **Coverage**: 85% of sold products

## Unit Conversions & Assumptions

### Energy Conversions
- **Natural Gas**: m³ to kWh (conversion factor: 10.55 kWh/m³)
- **Diesel**: L to kg (density: 0.832 kg/L)
- **Electricity**: Assumed 100% grid mix for location-based

### Assumptions Log References
- **A001**: Revenue estimation for Scope 3 Category 4
- **A002**: Employee commuting data extrapolation
- **A003**: Waste composition assumptions

## Market-based Policy

### Supplier-specific Contracts
- **Contract CON-2024-001**: 40,000 kWh renewable energy
- **Guarantees of Origin**: EU GO certificates
- **Power Purchase Agreement**: 10-year PPA for solar energy

### Residual Mix
- **Bavaria**: 0.280 kg CO₂e/kWh (2023 residual mix)
- **Calculation**: Remaining consumption after supplier-specific

## Calculation Metadata
- **Version**: Calc v1
- **Timestamp**: ${new Date().toLocaleString()}
- **Data Freeze**: data_freeze_v1
- **Factors Pinned**: Yes (all emission factors locked)`;
  };

  // Initialize with sample data if empty
  useEffect(() => {
    if (!formData.methodology || formData.methodology.trim() === '') {
      setFormData(prev => ({
        ...prev,
        methodology: generateSampleMethodology()
      }));
    }
  }, [formData.methodology]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGWPVersionChange = (version) => {
    setFormData(prev => ({
      ...prev,
      gwpVersion: version
    }));
  };

  const addAssumption = () => {
    const newAssumption = {
      id: Date.now(),
      code: `A${String(formData.assumptions.length + 1).padStart(3, '0')}`,
      description: '',
      impact: 'low',
      rationale: ''
    };

    setFormData(prev => ({
      ...prev,
      assumptions: [...prev.assumptions, newAssumption]
    }));
  };

  const handleAssumptionChange = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      assumptions: prev.assumptions.map(assumption => 
        assumption.id === id 
          ? { ...assumption, [field]: value }
          : assumption
      )
    }));
  };

  const removeAssumption = (id) => {
    setFormData(prev => ({
      ...prev,
      assumptions: prev.assumptions.filter(assumption => assumption.id !== id)
    }));
  };

  const processAutoLinks = (text) => {
    // Auto-link emission factors
    let processedText = text.replace(
      /(\d+\.\d+)\s*(kg CO₂e\/[A-Za-z]+)/g,
      '<span class="text-blue-600 hover:text-blue-800 cursor-pointer underline">$1 $2</span>'
    );

    // Auto-link assumption codes
    processedText = processedText.replace(
      /(A\d{3})/g,
      '<span class="text-green-600 hover:text-green-800 cursor-pointer underline">$1</span>'
    );

    // Auto-link contract references
    processedText = processedText.replace(
      /(CON-\d{4}-\d{3})/g,
      '<span class="text-purple-600 hover:text-purple-800 cursor-pointer underline">$1</span>'
    );

    return processedText;
  };







  const getErrorClass = (field) => {
    return errors[field] ? 'border-red-500 bg-red-50' : '';
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-8">
      <div className="flex items-center mb-6">
        <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mr-4">
          <BookOpen className="w-6 h-6 text-indigo-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{t('workflow.calculation.methodology.title')}</h2>
          <p className="text-gray-600">{t('workflow.calculation.methodology.description')}</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Calculation Metadata */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Tag className="w-4 h-4 text-indigo-600" />
                <span className="text-sm font-medium text-indigo-900">{formData.calculationVersion}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-indigo-600" />
                <span className="text-sm text-indigo-700">
                  {new Date(formData.calculationTimestamp).toLocaleString()}
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Settings className="w-4 h-4 text-indigo-600" />
              <span className="text-sm text-indigo-700">{t('workflow.calculation.methodology.metadata.factorsPinned')}</span>
            </div>
          </div>
        </div>

        {/* GWP Version Selection */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('workflow.calculation.methodology.gwpVersion.title')}</h3>
          <div className="space-y-3">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="gwpVersion"
                value="AR5"
                checked={formData.gwpVersion === 'AR5'}
                onChange={(e) => handleGWPVersionChange(e.target.value)}
                className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 focus:ring-indigo-500"
              />
              <div>
                <span className="font-medium text-gray-900">{t('workflow.calculation.methodology.gwpVersion.ar5')}</span>
                <p className="text-sm text-gray-600">{t('workflow.calculation.methodology.gwpVersion.ar5Description')}</p>
              </div>
            </label>
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="gwpVersion"
                value="AR6"
                checked={formData.gwpVersion === 'AR6'}
                onChange={(e) => handleGWPVersionChange(e.target.value)}
                className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 focus:ring-indigo-500"
              />
              <div>
                <span className="font-medium text-gray-900">{t('workflow.calculation.methodology.gwpVersion.ar6')}</span>
                <p className="text-sm text-gray-600">{t('workflow.calculation.methodology.gwpVersion.ar6Description')}</p>
              </div>
            </label>
          </div>
          {errors.gwpVersion && (
            <p className="text-sm text-red-600 mt-2 flex items-center">
              <AlertTriangle className="w-4 h-4 mr-1" />
              {errors.gwpVersion}
            </p>
          )}
        </div>

        {/* Methodology Rich Text */}
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">{t('workflow.calculation.methodology.documentation.title')}</h3>
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Link className="w-4 h-4" />
              <span>{t('workflow.calculation.methodology.documentation.autoLinksEnabled')}</span>
            </div>
          </div>
          
          <textarea
            value={formData.methodology}
            onChange={(e) => handleInputChange('methodology', e.target.value)}
            className={`w-full h-64 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm ${
              getErrorClass('methodology')
            }`}
            placeholder={t('workflow.calculation.methodology.documentation.placeholder')}
          />
          
          {errors.methodology && (
            <p className="text-sm text-red-600 mt-2 flex items-center">
              <AlertTriangle className="w-4 h-4 mr-1" />
              {errors.methodology}
            </p>
          )}

          {/* Preview with Auto-links */}
          {formData.methodology && (
            <div className="mt-4 p-4 bg-white border rounded-lg">
              <h4 className="font-medium text-gray-800 mb-2">{t('workflow.calculation.methodology.documentation.preview')}</h4>
              <div 
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ 
                  __html: processAutoLinks(formData.methodology.replace(/\n/g, '<br>')) 
                }}
              />
            </div>
          )}
        </div>

        {/* Boundaries & Exclusions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('workflow.calculation.methodology.boundaries.title')}</h3>
            <textarea
              value={formData.boundaries}
              onChange={(e) => handleInputChange('boundaries', e.target.value)}
              className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder={t('workflow.calculation.methodology.boundaries.placeholder')}
            />
            {warnings.boundaries && (
              <p className="text-sm text-yellow-600 mt-2 flex items-center">
                <Info className="w-4 h-4 mr-1" />
                {warnings.boundaries}
              </p>
            )}
          </div>

          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('workflow.calculation.methodology.exclusions.title')}</h3>
            <textarea
              value={formData.exclusions}
              onChange={(e) => handleInputChange('exclusions', e.target.value)}
              className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder={t('workflow.calculation.methodology.exclusions.placeholder')}
            />
            {warnings.exclusions && (
              <p className="text-sm text-yellow-600 mt-2 flex items-center">
                <Info className="w-4 h-4 mr-1" />
                {warnings.exclusions}
              </p>
            )}
          </div>
        </div>

        {/* Scope 3 Methods */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('workflow.calculation.methodology.scope3Methods.title')}</h3>
          <textarea
            value={formData.scope3Methods}
            onChange={(e) => handleInputChange('scope3Methods', e.target.value)}
            className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder={t('workflow.calculation.methodology.scope3Methods.placeholder')}
          />
        </div>

        {/* Unit Conversions & Assumptions */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('workflow.calculation.methodology.unitConversions.title')}</h3>
          <textarea
            value={formData.unitConversions}
            onChange={(e) => handleInputChange('unitConversions', e.target.value)}
            className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder={t('workflow.calculation.methodology.unitConversions.placeholder')}
          />
        </div>

        {/* Market-based Policy */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('workflow.calculation.methodology.marketBasedPolicy.title')}</h3>
          <textarea
            value={formData.mbPolicy}
            onChange={(e) => handleInputChange('mbPolicy', e.target.value)}
            className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder={t('workflow.calculation.methodology.marketBasedPolicy.placeholder')}
          />
        </div>

        {/* Assumptions Log */}
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">{t('workflow.calculation.methodology.assumptionsLog.title')}</h3>
            <button
              onClick={addAssumption}
              className="flex items-center space-x-2 px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm"
            >
              <FileText className="w-4 h-4" />
              <span>{t('workflow.calculation.methodology.assumptionsLog.addAssumption')}</span>
            </button>
          </div>

          <div className="space-y-4">
            {formData.assumptions.map((assumption) => (
              <div key={assumption.id} className="bg-white p-4 rounded border">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                      {assumption.code}
                    </span>
                    <select
                      value={assumption.impact}
                      onChange={(e) => handleAssumptionChange(assumption.id, 'impact', e.target.value)}
                      className="text-sm border border-gray-300 rounded px-2 py-1"
                    >
                      <option value="low">{t('workflow.calculation.methodology.assumptionsLog.impact.low')}</option>
                      <option value="medium">{t('workflow.calculation.methodology.assumptionsLog.impact.medium')}</option>
                      <option value="high">{t('workflow.calculation.methodology.assumptionsLog.impact.high')}</option>
                    </select>
                  </div>
                  <button
                    onClick={() => removeAssumption(assumption.id)}
                    className="text-red-600 hover:text-red-800 text-sm underline"
                  >
                    {t('workflow.calculation.kpis.remove')}
                  </button>
                </div>
                
                <div className="space-y-2">
                  <input
                    type="text"
                    value={assumption.description}
                    onChange={(e) => handleAssumptionChange(assumption.id, 'description', e.target.value)}
                    placeholder={t('workflow.calculation.methodology.assumptionsLog.assumptionDescription')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <textarea
                    value={assumption.rationale}
                    onChange={(e) => handleAssumptionChange(assumption.id, 'rationale', e.target.value)}
                    placeholder={t('workflow.calculation.methodology.assumptionsLog.rationalePlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    rows={2}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Validation Messages */}
        {Object.keys(errors).length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="text-red-800 font-medium mb-2">{t('common.errors')}</h4>
            <ul className="space-y-1">
              {Object.values(errors).map((error, index) => (
                <li key={index} className="text-red-700 text-sm flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {Object.keys(warnings).length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="text-yellow-800 font-medium mb-2">{t('common.warnings')}</h4>
            <ul className="space-y-1">
              {Object.values(warnings).map((warning, index) => (
                <li key={index} className="text-yellow-700 text-sm flex items-center">
                  <Info className="w-4 h-4 mr-2" />
                  {warning}
                </li>
              ))}
            </ul>
          </div>
        )}


      </div>
    </div>
  );
};

export default Methodology;
